import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { Copy, Grid2x2Plus, ImagePlus, Link2, Pencil, Plus, RotateCcw, Search, Share2, Shield, Sparkles, Star, TableProperties, Trash2, Users, X } from 'lucide-react';
import toast from 'react-hot-toast';
import sheetCardIcon from '../../assets/icons/icon.png';
import sheetLogo from '../../assets/images/sheetlogo.png';
import { storage } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { sheetFormService } from '../../services/sheetFormService';
import { sheetService } from '../../services/sheetService';
import { userService } from '../../services/userService';
import { sheetWorkspaceService } from '../../services/sheetWorkspaceService';
import { getSheetPath, getSheetUrl, getWorkspaceCreatePath, getWorkspacePath, normalizeWorkspaceCustomDomain } from '../../utils/sheetDomains';
import { buildSheetShareMetadata } from '../../utils/sheetShare';
import LoginModal from '../Auth/LoginModal';
import SheetsHeader from './SheetsHeader';

const VIEW_OPTIONS = [
  { id: 'owned', label: 'My Sheets', icon: TableProperties },
  { id: 'shared', label: 'Shared', icon: Users },
  { id: 'templates', label: 'Templates', icon: Sparkles }
];

const parseEmailList = (raw = '') => {
  const normalized = raw
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean)
    .filter((item, index, all) => all.indexOf(item) === index);
  return normalized.filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
};

const formatDate = (timestamp) => {
  if (!timestamp) return 'Unknown';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const getInitialView = (state) => (
  state?.initialView === 'shared' || state?.initialView === 'templates'
    ? state.initialView
    : 'owned'
);

const getCloneTitleSuggestion = (sheet) => `${sheet?.title || 'Untitled Sheet'} Copy`;
const getSheetTitleKey = (value = '') => `${value || ''}`.trim().toLowerCase();
const isPermissionDeniedError = (error) => (
  error?.code === 'permission-denied' ||
  `${error?.message || ''}`.toLowerCase().includes('missing or insufficient permissions')
);
const getCompactDescription = (value = '', max = 120) => {
  const normalized = `${value || ''}`.trim();
  if (!normalized) return '';
  return normalized.length > max ? `${normalized.slice(0, max - 1).trim()}...` : normalized;
};
const getCollaboratorAccessLabel = (access) => {
  if (access === 'edit_own_rows') return 'you can edit your own rows only';
  if (access === 'view_all_edit_own') return 'you can view all rows and edit your own';
  if (access === 'edit') return 'you can edit the sheet';
  return 'view only';
};

const getOwnerCollaboratorAccessLabel = (access) => {
  if (access === 'edit_own_rows') return 'own rows only';
  if (access === 'view_all_edit_own') return 'view all, edit own';
  if (access === 'edit') return 'edit sheet';
  return 'view only';
};

const parseRestrictedAccessChoice = (value = '') => {
  const normalized = `${value || ''}`.trim().toLowerCase();
  if (['2', 'edit_own_rows', 'own'].includes(normalized)) return 'edit_own_rows';
  if (['3', 'view_all_edit_own', 'view_all_edit_own_rows', 'all'].includes(normalized)) return 'view_all_edit_own';
  if (['4', 'edit', 'sheet'].includes(normalized)) return 'edit';
  return 'view';
};

const normalizeWorkspaceSlug = (value, fallback = 'workspace') => {
  const normalized = `${value || ''}`
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-')
    .slice(0, 64);
  return normalized || fallback;
};

const getSheetFormPath = (sheetId) => `/sheet-form/${sheetId}`;
const getSheetFormUrl = (sheetId) => `${window.location.origin}${getSheetFormPath(sheetId)}`;
const shouldShowInLibrary = (sheet) => (
  Boolean(sheet) &&
  !`${sheet?.workspaceId || ''}`.trim() &&
  sheet?.workspaceOnly !== true
);

const SheetsList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sheets, setSheets] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeView, setActiveView] = useState(getInitialView(location.state));
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [workspaceModalMode, setWorkspaceModalMode] = useState('');
  const [workspaceModalSheet, setWorkspaceModalSheet] = useState(null);
  const [workspaceFormTitle, setWorkspaceFormTitle] = useState('');
  const [workspaceFormDescription, setWorkspaceFormDescription] = useState('');
  const [workspaceFormSlug, setWorkspaceFormSlug] = useState('');
  const [workspaceFormCustomDomain, setWorkspaceFormCustomDomain] = useState('');
  const [workspaceLogoFile, setWorkspaceLogoFile] = useState(null);
  const [workspaceLogoPreview, setWorkspaceLogoPreview] = useState('');
  const [workspaceSlugEdited, setWorkspaceSlugEdited] = useState(false);
  const [workspaceSlugStatus, setWorkspaceSlugStatus] = useState({ state: 'idle', message: '' });
  const [workspaceTargetId, setWorkspaceTargetId] = useState('');
  const [workspaceModalSaving, setWorkspaceModalSaving] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [sheetFormsById, setSheetFormsById] = useState({});

  const requireAuth = (action) => {
    if (user) return true;
    setShowLoginModal(true);
    return false;
  };

  useEffect(() => {
    setActiveView(getInitialView(location.state));
  }, [location.state]);

  useEffect(() => {
    if (!user?.uid) return;

    let isActive = true;

    const loadWorkspaces = async () => {
      try {
        const [ownedWorkspaces, sharedSheets] = await Promise.all([
          sheetWorkspaceService.getUserWorkspaces(user.uid),
          sheetService.getSharedSheets(user.uid).catch(() => [])
        ]);
        let sharedWorkspaces = [];
        let sheetDerivedWorkspaces = [];

        try {
          sharedWorkspaces = await sheetWorkspaceService.getSharedWorkspaces(user.uid);
        } catch (sharedError) {
          console.warn('Shared workspaces could not be loaded:', sharedError);
        }

        const sharedWorkspaceIds = Array.from(new Set(
          (sharedSheets || [])
            .map((sheet) => `${sheet.workspaceId || ''}`.trim())
            .filter(Boolean)
        ));

        if (sharedWorkspaceIds.length) {
          const sharedWorkspaceLookups = await Promise.all(
            sharedWorkspaceIds.map(async (workspaceId) => {
              try {
                return await sheetWorkspaceService.getWorkspaceById(workspaceId);
              } catch (workspaceError) {
                const matchingSheet = (sharedSheets || []).find((sheet) => `${sheet.workspaceId || ''}`.trim() === workspaceId);
                if (!matchingSheet) return null;
                return {
                  id: workspaceId,
                  title: `${matchingSheet.workspaceTitle || 'Shared Workspace'}`.trim() || 'Shared Workspace',
                  slug: '',
                  description: '',
                  visibility: 'restricted',
                  sharedVia: 'sheet'
                };
              }
            })
          );

          sheetDerivedWorkspaces = sharedWorkspaceLookups
            .filter(Boolean)
            .map((workspace) => ({ ...workspace, sharedVia: workspace.sharedVia || 'sheet' }));
        }

        const data = [
          ...ownedWorkspaces,
          ...sharedWorkspaces,
          ...sheetDerivedWorkspaces
        ].reduce((acc, workspace) => {
          if (!workspace?.id || acc.some((item) => item.id === workspace.id)) return acc;
          acc.push(workspace);
          return acc;
        }, []);

        if (!isActive) return;
        setWorkspaces(data);
      } catch (err) {
        if (!isActive) return;
      }
    };

    loadWorkspaces();

    return () => {
      isActive = false;
    };
  }, [user?.uid]);

  useEffect(() => {
    let isActive = true;

    const loadAdminState = async () => {
      if (!user?.uid) {
        if (isActive) setIsAdmin(false);
        return;
      }

      try {
        const openUser = await userService.getUserProfile(user.uid).catch(() => null);
        if (isActive) {
          setIsAdmin(`${openUser?.role || ''}`.trim() === 'admin');
        }
      } catch {
        if (isActive) setIsAdmin(false);
      }
    };

    loadAdminState();

    return () => {
      isActive = false;
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!user) {
      setSheets([]);
      setLoading(false);
      return;
    }

    const loadSheets = async () => {
      setLoading(true);
      setError(null);
      try {
        let data = [];

        if (activeView === 'shared') {
          data = await sheetService.getSharedSheets(user.uid);
        } else if (activeView === 'templates') {
          data = await sheetService.getTemplateSheets(user.uid);
        } else {
          const [ownedSheets, adminAccessibleSheets] = await Promise.all([
            sheetService.getUserSheets(user.uid),
            isAdmin ? sheetService.getAdminAccessibleSheets().catch(() => []) : Promise.resolve([])
          ]);

          const visibleOwnedSheets = (ownedSheets || []).filter((sheet) => shouldShowInLibrary(sheet));
          const visibleAdminSheets = (adminAccessibleSheets || [])
            .filter((sheet) => shouldShowInLibrary(sheet))
            .map((sheet) => ({
              ...sheet,
              adminLibraryAccess: sheet.userId !== user.uid
            }));

          data = [...visibleOwnedSheets, ...visibleAdminSheets].reduce((acc, sheet) => {
            if (!sheet?.id || acc.some((item) => item.id === sheet.id)) return acc;
            acc.push(sheet);
            return acc;
          }, []);
        }

        setSheets((Array.isArray(data) ? data : []).filter((sheet) => shouldShowInLibrary(sheet)));
      } catch (err) {
        setError(err?.message || 'Failed to load sheets');
      } finally {
        setLoading(false);
      }
    };

    loadSheets();
  }, [activeView, isAdmin, user]);

  useEffect(() => {
    const targetSheetIds = (Array.isArray(sheets) ? sheets : [])
      .map((sheet) => `${sheet?.id || ''}`.trim())
      .filter(Boolean);

    if (!targetSheetIds.length) {
      setSheetFormsById({});
      return;
    }

    let isActive = true;

    const loadSheetForms = async () => {
      try {
        const summaries = await sheetFormService.getSheetFormSummaries(targetSheetIds);
        if (!isActive) return;
        setSheetFormsById(summaries);
      } catch (err) {
        if (!isActive) return;
        console.warn('Sheet form summaries could not be loaded:', err);
        setSheetFormsById({});
      }
    };

    loadSheetForms();

    return () => {
      isActive = false;
    };
  }, [sheets]);

  const isSharedView = activeView === 'shared';
  const isTemplateView = activeView === 'templates';
  const isOwnedView = activeView === 'owned';
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const visibleSheets = useMemo(() => {
    if (!normalizedSearchTerm) return sheets;

    return sheets.filter((sheet) => {
      const haystack = [
        sheet.title,
        sheet.description,
        sheet.workspaceTitle,
        sheet.visibility
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedSearchTerm);
    });
  }, [normalizedSearchTerm, sheets]);
  const isWorkspaceModalOpen = workspaceModalMode === 'create' || workspaceModalMode === 'assign';

  const reloadOwnedSheets = async () => {
    if (!user?.uid) return;
    const ownedSheets = await sheetService.getUserSheets(user.uid);
    const nextSheets = (ownedSheets || [])
      .filter((sheet) => shouldShowInLibrary(sheet))
      .reduce((acc, sheet) => {
        if (!sheet?.id || acc.some((item) => item.id === sheet.id)) return acc;
        acc.push(sheet);
        return acc;
      }, []);
    setSheets(nextSheets);
  };

  const resetWorkspaceModal = () => {
    setWorkspaceModalMode('');
    setWorkspaceModalSheet(null);
    setWorkspaceFormTitle('');
    setWorkspaceFormDescription('');
    setWorkspaceFormSlug('');
    setWorkspaceFormCustomDomain('');
    setWorkspaceLogoFile(null);
    setWorkspaceLogoPreview('');
    setWorkspaceSlugEdited(false);
    setWorkspaceSlugStatus({ state: 'idle', message: '' });
    setWorkspaceTargetId('');
    setWorkspaceModalSaving(false);
  };

  const openAssignWorkspaceModal = (sheet) => {
    setWorkspaceModalMode('assign');
    setWorkspaceModalSheet(sheet);
    setWorkspaceTargetId(`${sheet?.workspaceId || ''}`.trim());
    setWorkspaceFormTitle('');
    setWorkspaceFormDescription('');
    setWorkspaceFormCustomDomain('');
  };

  useEffect(() => {
    if (!workspaceLogoFile) {
      setWorkspaceLogoPreview('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(workspaceLogoFile);
    setWorkspaceLogoPreview(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [workspaceLogoFile]);

  useEffect(() => {
    if (workspaceModalMode !== 'create') return;
    if (workspaceSlugEdited) return;
    setWorkspaceFormSlug(normalizeWorkspaceSlug(workspaceFormTitle));
  }, [workspaceFormTitle, workspaceModalMode, workspaceSlugEdited]);

  useEffect(() => {
    if (workspaceModalMode !== 'create') return undefined;

    const normalizedSlug = normalizeWorkspaceSlug(workspaceFormSlug, '');
    if (!normalizedSlug) {
      setWorkspaceSlugStatus({ state: 'idle', message: 'Enter a workspace path.' });
      return undefined;
    }

    let isActive = true;
    setWorkspaceSlugStatus({ state: 'checking', message: 'Checking availability...' });

    const timeoutId = setTimeout(async () => {
      try {
        await sheetWorkspaceService.getWorkspaceBySlug(normalizedSlug);
        if (!isActive) return;
        setWorkspaceSlugStatus({ state: 'unavailable', message: 'Path already exists. Edit the path to continue.' });
      } catch (error) {
        if (!isActive) return;
        setWorkspaceSlugStatus({ state: 'available', message: 'Path is available.' });
      }
    }, 250);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [workspaceFormSlug, workspaceModalMode]);

  const updateSheetCard = (sheetId, updates) => {
    setSheets((prev) => prev.map((item) => (
      item.id === sheetId ? { ...item, ...updates } : item
    )));
  };

  const handleOpenSheetForm = async (sheet) => {
    const cachedForm = sheetFormsById?.[sheet?.id];
    if (cachedForm) {
      window.open(getSheetFormUrl(sheet.id), '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      const form = await sheetFormService.getSheetFormById(sheet.id);
      if (!form) {
        toast.error('This sheet does not have a published form yet.');
        return;
      }
      setSheetFormsById((prev) => ({ ...prev, [sheet.id]: form }));
      window.open(getSheetFormUrl(sheet.id), '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err?.message || 'Unable to open the sheet form.');
    }
  };

  const handleShare = async (sheet) => {
    if (sheet.visibility === 'private') {
      toast.error('Private sheets cannot be shared by link.');
      return;
    }

    let shareMetadata = buildSheetShareMetadata(sheet, {
      workspaceSlug: sheet.workspaceSlug || '',
      customDomain: sheet.customDomain || ''
    });

    try {
      const resolvedMetadata = await sheetService.getSheetShareMetadata({ sheetId: sheet.id });
      shareMetadata = {
        ...shareMetadata,
        ...resolvedMetadata
      };
    } catch (err) {
      console.warn('Falling back to local sheet share metadata:', err);
    }

    const url = shareMetadata.url || getSheetUrl(sheet.id, sheet.workspaceSlug || '');
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareMetadata.shareTitle || shareMetadata.title || sheet.title,
          text: shareMetadata.shareText || shareMetadata.description || '',
          url
        });
        return;
      } catch (err) {
        console.warn('Share cancelled:', err);
      }
    }

    await navigator.clipboard.writeText(url);
    toast.success(
      sheet.visibility === 'restricted'
        ? 'Restricted sheet link copied. Only allowed users can open it.'
        : 'Share link copied to clipboard.'
    );
  };

  const handleSetVisibility = async (sheet, nextVisibility) => {
    if (!nextVisibility || nextVisibility === sheet.visibility) return;

    try {
      if (nextVisibility === 'restricted') {
        const current = Array.isArray(sheet.allowedEmails) ? sheet.allowedEmails.join(', ') : '';
        const raw = window.prompt('Enter allowed emails (comma-separated) for restricted access:', current);
        if (raw === null) return;
        const allowedEmails = parseEmailList(raw);
        if (!allowedEmails.length) {
          toast.error('Add at least one valid email for restricted access.');
          return;
        }

        const accessChoice = window.prompt(
          'Choose restricted access: 1 = view only, 2 = can edit own rows only, 3 = view all rows and edit own, 4 = view and edit sheet (no delete).',
          '1'
        );
        if (accessChoice === null) return;
        const updates = {
          visibility: 'restricted',
          allowedEmails,
          collaboratorAccess: parseRestrictedAccessChoice(accessChoice)
        };
        const savedUpdates = await sheetService.updateSheet(sheet.id, updates);
        await sheetService.publishSheet(sheet.id, {
          ...sheet,
          ...updates,
          ...savedUpdates
        }, user?.uid);
        updateSheetCard(sheet.id, { ...updates, ...savedUpdates });
        toast.success('Sheet access is now restricted.');
        return;
      }

      const updates = { visibility: nextVisibility, allowedEmails: [], collaboratorAccess: 'view' };
      const savedUpdates = await sheetService.updateSheet(sheet.id, updates);
      await sheetService.publishSheet(sheet.id, {
        ...sheet,
        ...updates,
        ...savedUpdates
      }, user?.uid);
      updateSheetCard(sheet.id, { ...updates, ...savedUpdates });
      toast.success(`Sheet is now ${nextVisibility}.`);
    } catch (err) {
      toast.error(err?.message || 'Failed to update visibility.');
    }
  };

  const handleTemplateToggle = async (sheet) => {
    const nextIsTemplate = !sheet.isTemplate;
    try {
      const updates = { isTemplate: nextIsTemplate };
      const savedUpdates = await sheetService.updateSheet(sheet.id, updates);
      await sheetService.publishSheet(sheet.id, {
        ...sheet,
        ...updates,
        ...savedUpdates
      }, user?.uid);
      if (isTemplateView && !nextIsTemplate) {
        setSheets((prev) => prev.filter((item) => item.id !== sheet.id));
      } else {
        updateSheetCard(sheet.id, { ...updates, ...savedUpdates });
      }
      toast.success(nextIsTemplate ? 'Sheet published as a template.' : 'Sheet removed from templates.');
    } catch (err) {
      toast.error(err?.message || 'Failed to update template status.');
    }
  };

  const handleDelete = async (sheet) => {
    const confirmed = window.confirm(`Delete "${sheet.title}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      await sheetService.deleteSheet(sheet.id);
      setSheets((prev) => prev.filter((item) => item.id !== sheet.id));
      toast.success('Sheet deleted.');
    } catch (err) {
      toast.error(err?.message || 'Failed to delete sheet.');
    }
  };

  const handleClone = async (sheet) => {
    const suggestedTitle = getCloneTitleSuggestion(sheet);
    const requestedTitle = window.prompt('Name the cloned sheet:', suggestedTitle);
    if (requestedTitle === null) return;

    const cleanTitle = requestedTitle.trim();
    if (!cleanTitle) {
      toast.error('A title is required to clone a sheet.');
      return;
    }

    try {
      const ownedSheets = await sheetService.getUserSheets(user.uid).catch(() => []);
      const cleanTitleKey = getSheetTitleKey(cleanTitle);
      const hasDuplicateTitle = (Array.isArray(ownedSheets) ? ownedSheets : []).some((ownedSheet) => (
        ownedSheet?.id !== sheet.id && getSheetTitleKey(ownedSheet?.title) === cleanTitleKey
      ));
      if (hasDuplicateTitle) {
        toast.error(`A sheet named "${cleanTitle}" already exists.`);
        return;
      }

      const clonedSheet = await sheetService.cloneSheet(sheet.id, user?.uid, { title: cleanTitle });
      if (isOwnedView) {
        setSheets((prev) => [clonedSheet, ...prev]);
      }
      toast.success(isTemplateView ? 'Template cloned into your sheets.' : 'Sheet cloned.');
      navigate(`/sheets/${clonedSheet.id}/edit`);
    } catch (err) {
      // cloneSheet can finish creating the main sheet before a legacy/optional
      // child document rejects access. Recover the completed clone so it is
      // visible and opens without requiring a page refresh.
      if (isPermissionDeniedError(err)) {
        try {
          const ownedSheets = await sheetService.getUserSheets(user?.uid);
          const recoveredClone = (Array.isArray(ownedSheets) ? ownedSheets : []).find((ownedSheet) => (
            getSheetTitleKey(ownedSheet?.title) === getSheetTitleKey(cleanTitle) &&
            (
              ownedSheet?.templateClonedFromId === sheet.id ||
              ownedSheet?.originalDocumentId === (sheet.originalDocumentId || sheet.id)
            )
          ));

          if (recoveredClone) {
            setSheets((prev) => [
              recoveredClone,
              ...prev.filter((item) => item.id !== recoveredClone.id)
            ]);
            toast.success(isTemplateView ? 'Template cloned into your sheets.' : 'Sheet cloned.');
            navigate(`/sheets/${recoveredClone.id}/edit`);
            return;
          }
        } catch (recoveryError) {
          console.warn('Completed sheet clone could not be recovered immediately:', recoveryError);
        }
      }
      toast.error(err?.message || 'Failed to clone sheet.');
    }
  };

  const handleWorkspaceModalSubmit = async () => {
    if (workspaceModalMode === 'create') {
      const cleanTitle = workspaceFormTitle.trim();
      const cleanSlug = normalizeWorkspaceSlug(workspaceFormSlug, '');
      const cleanCustomDomain = normalizeWorkspaceCustomDomain(workspaceFormCustomDomain);
      if (!cleanTitle) {
        toast.error('Workspace title is required.');
        return;
      }
      if (!cleanSlug) {
        toast.error('Workspace path is required.');
        return;
      }
      if (workspaceSlugStatus.state === 'unavailable') {
        toast.error('Choose an available workspace path.');
        return;
      }

      setWorkspaceModalSaving(true);
      try {
        const workspace = await sheetWorkspaceService.createWorkspace({
          title: cleanTitle,
          slug: cleanSlug,
          customDomain: cleanCustomDomain || undefined,
          description: workspaceFormDescription.trim()
        }, user?.uid);
        let logoUrl = '';

        if (workspaceLogoFile && user?.uid) {
          const safeName = `${Date.now()}_${workspaceLogoFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
          const logoRef = ref(storage, `sheets/workspaces/${workspace.id}/logo/${safeName}`);
          const snapshot = await uploadBytes(logoRef, workspaceLogoFile);
          logoUrl = await getDownloadURL(snapshot.ref);
          await sheetWorkspaceService.updateWorkspace(workspace.id, { logoUrl });
        }

        const createdWorkspace = logoUrl ? { ...workspace, logoUrl } : workspace;
        if (workspaceModalSheet?.id) {
          await sheetService.cloneSheet(workspaceModalSheet.id, user?.uid, {
            title: workspaceModalSheet.title,
            workspaceId: createdWorkspace.id,
            workspaceTitle: createdWorkspace.title || '',
            workspaceOnly: true
          });
          if (isOwnedView) {
            await reloadOwnedSheets();
          }
        }
        setWorkspaces((prev) => [createdWorkspace, ...prev]);
        resetWorkspaceModal();
        if (workspaceModalSheet?.id) {
          toast.success(`Workspace created and ${workspaceModalSheet.title || 'sheet'} was cloned into it.`);
        } else {
          navigate(getWorkspacePath(createdWorkspace.slug));
          toast.success('Workspace created.');
        }
      } catch (err) {
        toast.error(err?.message || 'Failed to create workspace.');
        setWorkspaceModalSaving(false);
      }
      return;
    }

    if (workspaceModalMode === 'assign' && workspaceModalSheet) {
      if (!workspaceTargetId) {
        toast.error('Choose a workspace.');
        return;
      }

      const targetWorkspace = workspaces.find((workspace) => workspace.id === workspaceTargetId);
      if (!targetWorkspace) {
        toast.error('Workspace not found.');
        return;
      }

      setWorkspaceModalSaving(true);
      try {
        await sheetService.cloneSheet(workspaceModalSheet.id, user?.uid, {
          title: workspaceModalSheet.title,
          workspaceId: targetWorkspace.id,
          workspaceTitle: targetWorkspace.title || '',
          workspaceOnly: true
        });
        if (isOwnedView) {
          await reloadOwnedSheets();
        }
        resetWorkspaceModal();
        toast.success(`Cloned into ${targetWorkspace.title}.`);
      } catch (err) {
        toast.error(err?.message || 'Failed to add sheet to workspace.');
        setWorkspaceModalSaving(false);
      }
    }
  };

  const handleRemoveFromWorkspace = async (sheet) => {
    if (!sheet.workspaceId) return;
    const confirmed = window.confirm(`Remove "${sheet.title}" from its workspace?`);
    if (!confirmed) return;

    try {
      await sheetService.updateSheet(sheet.id, {
        workspaceId: null,
        workspaceTitle: null,
        workspaceOnly: false
      });
      updateSheetCard(sheet.id, {
        workspaceId: null,
        workspaceTitle: null,
        workspaceOnly: false
      });
      toast.success('Removed from workspace.');
    } catch (err) {
      toast.error(err?.message || 'Failed to remove sheet from workspace.');
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto space-y-4">
      <SheetsHeader
        activeTab="sheets"
        badge="Sheet Libary"
        showBackButton={false}
        badgeActions={(
          <button
            type="button"
            title="Pricing"
            aria-label="Pricing"
            onClick={() => navigate('/sheets/pricing')}
            className="inline-flex items-center justify-center rounded-full border border-[#008080] bg-white px-2.5 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#5f7a67] transition-colors hover:bg-slate-50 sm:gap-2 sm:px-3 sm:py-1"
          >
            <Link2 className="h-3.5 w-3.5" />
            <span className="sr-only sm:not-sr-only">Pricing</span>
          </button>
        )}
        
        description="Build client-ready sheets, branded workspaces, and reusable templates from one focused product surface."
        onSignInClick={() => setShowLoginModal(true)}
        actions={(
          <div className="flex flex-wrap items-center justify-end gap-3">
            {VIEW_OPTIONS.map((view) => {
              const Icon = view.icon;
              const isActive = activeView === view.id;
              return (
                <button
                  key={view.id}
                  type="button"
                  aria-label={view.label}
                  onClick={() => {
                    if (!requireAuth()) return;
                    setActiveView(view.id);
                  }}
                  className={`inline-flex items-center justify-center rounded-xl border px-3 py-3 text-sm font-semibold transition-colors sm:gap-2 sm:px-5 ${
                    isActive
                      ? 'border-[#008080]/30 bg-[#008080]/10 text-[#008080] hover:bg-[#008080]/15'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="sr-only sm:not-sr-only">{view.label}</span>
                </button>
              );
            })}
            <button
              type="button"
              aria-label="New Sheet"
              onClick={() => requireAuth() && navigate('/sheets/new')}
              className="inline-flex items-center justify-center rounded-xl bg-[#008080] px-3 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#006d6d] sm:gap-2 sm:px-6"
            >
              <Plus className="h-4 w-4" />
              <span className="sr-only sm:not-sr-only">New Sheet</span>
            </button>
          </div>
        )}
      />

      <div className="rounded-[10px] border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search sheets..."
            aria-label="Search sheets"
            className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-4 text-sm text-slate-700 outline-none transition focus:border-[#008080]/40"
          />
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
            <span className="rounded-full bg-slate-100 px-3 py-1.5 text-slate-700">
              {visibleSheets.length} {visibleSheets.length === 1 ? 'sheet' : 'sheets'}
            </span>
            {normalizedSearchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="rounded-full border border-slate-200 px-3 py-1.5 text-slate-600 transition hover:bg-slate-50"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {loading && (
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-10 text-center text-slate-500 shadow-sm">
          Loading sheets...
        </div>
      )}

      {!loading && error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-6 py-5 text-rose-700 shadow-sm">
          {error}
        </div>
      )}

      {!loading && !error && visibleSheets.length === 0 && (
        <div className="rounded-[10px] border border-slate-200 bg-white px-8 py-14 text-center shadow-md">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#008080]/10 text-[#008080]">
            <TableProperties className="h-7 w-7" />
          </div>
          <h2 className="mt-5 text-2xl font-semibold text-slate-900">
            {!user
              ? 'Sign in to get started'
              : searchTerm.trim()
                ? 'No sheets match your search'
              : isSharedView ? 'No shared sheets yet' : isTemplateView ? 'No templates yet' : 'No sheets yet'}
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-slate-500">
            {!user
              ? 'Sign in to create sheets, collaborate with others, and access templates.'
              : searchTerm.trim()
                ? 'Try a different title, description, or workspace keyword.'
              : isSharedView
                ? 'Sheets shared directly to your account will appear here.'
                : isTemplateView
                  ? 'Publish one of your sheets as a template and it will appear here for every authenticated user to clone.'
                  : 'Define headers once, start with one row, and keep adding row documents as the sheet grows.'}
          </p>
          {!isSharedView && !isTemplateView && (
            <button
              onClick={() => requireAuth() && navigate('/sheets/new')}
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#008080] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#006d6d]"
            >
              <Plus className="h-4 w-4" />
              New Sheet
            </button>
          )}
        </div>
      )}

      {!loading && !error && visibleSheets.length > 0 && (
        <main className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {visibleSheets.map((sheet) => {
            const isOwnedByCurrentUser = Boolean(user?.uid && sheet.userId === user.uid);
            const isAdminAccessibleSheet = Boolean(sheet.adminLibraryAccess && !isOwnedByCurrentUser);
            const hasSheetForm = Boolean(sheetFormsById?.[sheet.id]) || isOwnedByCurrentUser;
            return (
              <article
                key={sheet.id}
                className="group flex h-full flex-col overflow-hidden rounded-[10px] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-[#008080]/30 hover:shadow-lg"
              >
                <div className="h-1 bg-[#008080] opacity-80 transition group-hover:opacity-100" />
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-slate-200 bg-slate-50">
                      <img src={sheetCardIcon} alt="" className="h-full w-full object-contain p-2" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h2 className="truncate text-lg font-bold leading-6 text-slate-900">{sheet.title || 'Untitled Sheet'}</h2>
                          <p className="mt-1 text-xs font-medium text-slate-500">
                            Updated {formatDate(sheet.updatedAt || sheet.createdAt)}
                          </p>
                        </div>
                        {isOwnedView && isOwnedByCurrentUser && (
                          <select
                            value={sheet.visibility || 'private'}
                            onChange={(e) => handleSetVisibility(sheet, e.target.value)}
                            className="w-[112px] shrink-0 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs font-semibold text-slate-700 outline-none transition focus:border-[#008080]/40"
                            aria-label={`${sheet.title || 'Sheet'} visibility`}
                          >
                            <option value="private">Private</option>
                            <option value="public">Public</option>
                            <option value="restricted">Restricted</option>
                          </select>
                        )}
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        {isAdminAccessibleSheet ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-violet-700">
                            <Shield className="h-3.5 w-3.5" />
                            Admin
                          </span>
                        ) : !isOwnedByCurrentUser && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-sky-700">
                            <Users className="h-3.5 w-3.5" />
                            Shared
                          </span>
                        )}
                        {sheet.isTemplate && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-700">
                            <Star className="h-3.5 w-3.5" />
                            Template
                          </span>
                        )}
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600">
                          {sheet.visibility || 'private'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="mt-4 min-h-[60px] text-sm leading-5 text-slate-600">
                    {getCompactDescription(
                      sheet.description || 'No description yet. Add structure, formulas, and rows from this sheet card.',
                      135
                    )}
                  </p>

                  <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <p className="font-semibold text-slate-900">{sheet.headers?.length || 0}</p>
                      <p className="mt-0.5 text-slate-500">Columns</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <p className="truncate font-semibold text-slate-900">
                        {sheet.workspaceTitle && !sheet.workspaceOnly ? sheet.workspaceTitle : 'Library'}
                      </p>
                      <p className="mt-0.5 text-slate-500">Location</p>
                    </div>
                  </div>

                  <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                    {isTemplateView ? (
                      <p className="text-sm font-medium leading-5 text-[#008080]">
                        {isOwnedByCurrentUser
                          ? 'Your template. Signed-in users can clone it into their own library.'
                          : 'Signed-in users can clone this template and become the new owner'}
                      </p>
                    ) : isAdminAccessibleSheet ? (
                      <p className="text-sm font-medium leading-5 text-violet-700">
                        Admin builder access. Maintenance entry only.
                      </p>
                    ) : isSharedView || (isOwnedView && !isOwnedByCurrentUser) ? (
                      <p className="text-sm font-medium leading-5 text-[#008080]">
                        Shared with you. {getCollaboratorAccessLabel(sheet.sharedAccess)}
                      </p>
                    ) : sheet.visibility === 'restricted' ? (
                      <p className="text-sm font-medium leading-5 text-amber-700">
                        {Array.isArray(sheet.allowedEmails) ? sheet.allowedEmails.length : 0} allowed email(s)
                        {`. ${getOwnerCollaboratorAccessLabel(sheet.collaboratorAccess)}.`}
                      </p>
                    ) : (
                      <p className="text-sm font-medium leading-5 text-slate-600">
                        {sheet.visibility === 'public' ? 'Public sheet' : 'Private sheet'}
                        {sheet.isTemplate ? '. Also published as a reusable template.' : ''}
                      </p>
                    )}
                  </div>

                <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
                  <button
                    onClick={() => navigate(getSheetPath(sheet.id, sheet.workspaceSlug || ''), {
                      state: isSharedView ? { fromSharedSheets: true } : undefined
                    })}
                    className="inline-flex items-center justify-center rounded-lg bg-[#008080] px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#006d6d]"
                  >
                    Open
                  </button>
                  {hasSheetForm && (
                    <button
                      onClick={() => handleOpenSheetForm(sheet)}
                      className="inline-flex items-center justify-center rounded-lg border border-[#008080]/20 bg-[#008080]/5 px-3 py-2.5 text-xs font-semibold text-[#008080] transition-colors hover:bg-[#008080]/10"
                    >
                      Form
                    </button>
                  )}
                  {(isTemplateView || isOwnedView) && (
                    <button
                      onClick={() => handleClone(sheet)}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                    >
                      <Copy className="h-4 w-4" />
                      Clone
                    </button>
                  )}
                  {isOwnedView && isAdminAccessibleSheet && (
                    <button
                      onClick={() => navigate(`/sheets/${sheet.id}/edit`, {
                        state: {
                          returnTo: '/sheets'
                        }
                      })}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-violet-600 px-3 py-2.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-violet-700"
                    >
                      <Pencil className="h-4 w-4" />
                      Open Builder
                    </button>
                  )}
                  {isOwnedView && isOwnedByCurrentUser && (
                    <>
                      <button
                        onClick={() => navigate(`/sheets/${sheet.id}/edit`, {
                          state: {
                            returnTo: '/sheets'
                          }
                        })}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                      >
                        <Pencil className="h-4 w-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleShare(sheet)}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                      >
                        <Share2 className="h-4 w-4" />
                        Share
                      </button>
                      <button
                        onClick={() => openAssignWorkspaceModal(sheet)}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                      >
                        <Grid2x2Plus className="h-4 w-4" />
                        {sheet.workspaceId ? 'Move Workspace' : 'Add To Workspace'}
                      </button>
                      {sheet.workspaceId && (
                        <button
                          onClick={() => handleRemoveFromWorkspace(sheet)}
                          className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-2.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                        >
                          Remove From Workspace
                        </button>
                      )}
                      <button
                        onClick={() => handleTemplateToggle(sheet)}
                        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${
                          sheet.isTemplate
                            ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
                            : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <Star className="h-4 w-4" />
                        {sheet.isTemplate ? 'Remove Template' : 'Publish Template'}
                      </button>
                      <button
                        onClick={() => handleDelete(sheet)}
                        className="ml-auto inline-flex items-center justify-center gap-2 rounded-lg border border-rose-100 px-3 py-2.5 text-xs font-semibold text-rose-600 transition-colors hover:bg-rose-50"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </button>
                    </>
                  )}
                </div>
                </div>
              </article>
            );
          })}
        </main>
      )}

      {isWorkspaceModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
          <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-xl flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_32px_90px_-40px_rgba(15,23,42,0.55)]">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#008080]">
                  {workspaceModalMode === 'create' ? 'New Workspace' : 'Assign Workspace'}
                </p>
                <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-900">
                  {workspaceModalMode === 'create'
                    ? 'Create a workspace'
                    : `Add ${workspaceModalSheet?.title || 'sheet'} to a workspace`}
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  {workspaceModalMode === 'create'
                    ? 'Workspaces get a unique URL path and group related sheets, references, and logs.'
                    : 'Choose a workspace for this sheet. It will still remain visible in the main library.'}
                </p>
              </div>
              <button
                type="button"
                onClick={resetWorkspaceModal}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                aria-label="Close workspace dialog"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-5 overflow-y-auto px-6 py-6">
              {workspaceModalMode === 'create' ? (
                <>
                  <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                    <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white">
                      <img
                        src={workspaceLogoPreview || sheetLogo}
                        alt="Workspace logo preview"
                        className="h-full w-full object-contain p-2"
                      />
                    </div>
                    <div className="min-w-0 flex-1 space-y-3">
                      <div>
                        <p className="text-sm font-medium text-slate-700">Workspace Logo</p>
                        <p className="mt-1 text-xs text-slate-500">
                          New workspaces start with the sheet logo. Upload another image if needed.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (!file.type.startsWith('image/')) {
                                toast.error('Choose an image file for the workspace logo.');
                                e.target.value = '';
                                return;
                              }
                              setWorkspaceLogoFile(file);
                              e.target.value = '';
                            }}
                          />
                          <ImagePlus className="h-4 w-4" />
                          Upload Logo
                        </label>
                        <button
                          type="button"
                          onClick={() => setWorkspaceLogoFile(null)}
                          className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                        >
                          <RotateCcw className="h-4 w-4" />
                          Use Default
                        </button>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Workspace Name</label>
                    <input
                      value={workspaceFormTitle}
                      onChange={(e) => setWorkspaceFormTitle(e.target.value)}
                      placeholder="Client Ops"
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                    />
                    <p className="mt-2 text-xs text-slate-500">
                      The workspace name and path can be edited independently.
                    </p>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Workspace Path</label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400">
                        /sheets/workspace/
                      </span>
                      <input
                        value={workspaceFormSlug}
                        onChange={(e) => {
                          setWorkspaceSlugEdited(true);
                          setWorkspaceFormSlug(normalizeWorkspaceSlug(e.target.value, ''));
                        }}
                        placeholder="client-ops"
                        className="w-full rounded-2xl border border-slate-200 py-3 pl-[9.5rem] pr-4 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                      />
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <Link2 className="h-3.5 w-3.5 text-slate-400" />
                      <span className={
                        workspaceSlugStatus.state === 'available'
                          ? 'text-emerald-600'
                          : workspaceSlugStatus.state === 'unavailable'
                            ? 'text-rose-600'
                            : 'text-slate-500'
                      }>
                        {workspaceSlugStatus.message || 'Enter a workspace path.'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Description</label>
                    <textarea
                      value={workspaceFormDescription}
                      onChange={(e) => setWorkspaceFormDescription(e.target.value)}
                      rows={3}
                      placeholder="Optional context for this workspace"
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Custom Domain</label>
                    <input
                      value={workspaceFormCustomDomain}
                      onChange={(e) => setWorkspaceFormCustomDomain(e.target.value)}
                      placeholder="workspace.yourdomain.com"
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                    />
                    <p className="mt-2 text-xs text-slate-500">
                      Optional. Point this domain to the sheets app to open the workspace directly.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Select Workspace</label>
                    <select
                      value={workspaceTargetId}
                      onChange={(e) => setWorkspaceTargetId(e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#008080]"
                    >
                      <option value="">Choose a workspace</option>
                      {workspaces.map((workspace) => (
                        <option key={`assign_workspace_${workspace.id}`} value={workspace.id}>
                          {workspace.title} ({workspace.slug})
                        </option>
                      ))}
                    </select>
                  </div>
                  {workspaces.length === 0 && (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                      No workspaces exist yet. Create one first.
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      navigate(getWorkspaceCreatePath(), {
                        state: {
                          assignSheetId: workspaceModalSheet?.id,
                          assignSheetTitle: workspaceModalSheet?.title || ''
                        }
                      });
                    }}
                    className="inline-flex items-center gap-2 rounded-2xl border border-dashed border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-[#008080]/40 hover:text-[#008080]"
                  >
                    <Plus className="h-4 w-4" />
                    Create A New Workspace Instead
                  </button>
                </>
              )}
            </div>

            <div className="flex shrink-0 items-center justify-end gap-3 border-t border-slate-200 px-6 py-5">
              <button
                type="button"
                onClick={resetWorkspaceModal}
                className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleWorkspaceModalSubmit}
                disabled={workspaceModalSaving || (workspaceModalMode === 'assign' && workspaces.length === 0)}
                className="rounded-2xl bg-[#008080] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#006d6d] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {workspaceModalSaving
                  ? (workspaceModalMode === 'create' ? 'Creating...' : 'Saving...')
                  : (workspaceModalMode === 'create' ? 'Create Workspace' : 'Add To Workspace')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLoginModal && (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
          onSuccess={() => setShowLoginModal(false)}
          logo="sheets"
        />
      )}
    </div>
  );
};

export default SheetsList;
